import { UsageTrackerEvent, usageTracker } from "@volumental/analytic";
import { Content, Headline, StarRating } from "@volumental/common";
import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import type { JSX } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { SizeLocale } from "../../../api/gql/graphql";
import { submitFeedbackMutation } from "../../../api/mutations/submit-feedback-mutation";
import { Modal } from "../../../components/modal/modal";
import { useScannerStore, useSessionStore, useViewStore } from "../../../store";
import { FeedbackButtons } from "./feedback-buttons";
import { FeedbackCommentBox } from "./feedback-comment-box";

interface FeedbackProps {
  show: boolean;
  onClose: (feedback: boolean) => void;
  closeModal: () => void;
  pdpDetail?: { local: SizeLocale; gender?: string; recommendedSize: string };
  scanID?: string;
  previousPurchaseID?: string;
  trackingEvent?: UsageTrackerEvent;
  title?: string;
}

export function Feedback({
  show,
  onClose,
  closeModal,
  pdpDetail,
  scanID,
  previousPurchaseID,
  trackingEvent = UsageTrackerEvent.Feedback,
  title,
}: FeedbackProps): JSX.Element {
  const { mode, productID } = useSessionStore((store) => store);
  const { currentView } = useViewStore((store) => store);
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const { flowType } = useScannerStore((store) => store);
  const [submitFeedback] = useMutation(submitFeedbackMutation);

  const ratingNPSTitles: string[] = t("feedbackModal.npsSubtitle", {
    returnObjects: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const uploadFeeback = async (): Promise<void> => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    // uploadFeedback
    const feedback: Record<string, string | undefined> = {
      Rating: rating.toString(),
      Comment: comment,
      recommended_size: pdpDetail?.recommendedSize,
      productID: productID ?? "UNKNOWN",
      size_local: pdpDetail?.local,
      product_gender: pdpDetail?.gender,
      sizing_flow_type: flowType ?? "UNKNOWN",
      previous_purchase_id: previousPurchaseID ?? "NO ID",
      scanID: scanID ?? "NO ID",
      experience: mode,
      current_page: currentView ?? "UNKNOWN",
    };
    try {
      await Promise.all([
        trackingEvent !== UsageTrackerEvent.ExitScanning
          ? submitFeedback({
              variables: {
                input: {
                  rating: rating > 0 ? rating : null,
                  comment: comment || null,
                  scanId: scanID ?? null,
                  previousPurchaseId: previousPurchaseID ?? null,
                },
              },
            })
          : Promise.resolve(),
        trackingEvent === UsageTrackerEvent.ExitScanning
          ? usageTracker.trackAndFlush(trackingEvent, feedback)
          : Promise.resolve(usageTracker.track(trackingEvent, feedback)),
      ]);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn(`Failed to submit feedback: ${trackingEvent}`, error);
      }
    } finally {
      setIsSubmitting(false);
      onClose(true);
    }
  };
  const noAnswer = (): void => {
    onClose(false);
  };

  return (
    <Modal show={show} close={isSubmitting ? undefined : closeModal} modalTitle={t("feedbackModal.ratingTitle")}>
      <Content disableGutters disableScrollbar>
        <Headline className="text-base font-normal leading-normal mt-0">
          <Trans>{title ?? t("feedbackModal.ratingTitle")}</Trans>
        </Headline>
        <div className="pt-6 pb-5 flex justify-center">
          <StarRating
            onRatingSelect={(userRating) => {
              setRating(userRating);
            }}
            npsTitles={ratingNPSTitles}
            altImage={t("imageAlt.starIcon")}
          />
        </div>
        <FeedbackCommentBox
          setComment={(userComment) => {
            setComment(userComment);
          }}
        />
      </Content>
      <FeedbackButtons
        onSubmit={uploadFeeback}
        onSkip={noAnswer}
        isSubmitButtonDisabled={comment.trim() === "" && rating === 0}
        isSubmitting={isSubmitting}
      />
    </Modal>
  );
}
